import type { Position } from '@/types';

/**
 * Formations for an eleven.
 *
 * The API enforces formation bands on the starting eleven — exactly one
 * goalkeeper, 3–5 defenders, 2–5 midfielders and 1–3 forwards. This list is
 * every shape those bands allow, so the app offers exactly what the server
 * accepts: nothing picked here is refused, and nothing legal is missing.
 */

export interface Formation {
  /** Outfield shape, as everyone writes it: DEF-MID-FWD. */
  id: string;
  GK: number;
  DEF: number;
  MID: number;
  FWD: number;
}

const shape = (def: number, mid: number, fwd: number): Formation => ({
  id: `${def}-${mid}-${fwd}`,
  GK: 1,
  DEF: def,
  MID: mid,
  FWD: fwd,
});

/** Ordered most to least common, so the default sits first. */
export const FORMATIONS: readonly Formation[] = [
  shape(4, 4, 2),
  shape(4, 3, 3),
  shape(3, 5, 2),
  shape(3, 4, 3),
  shape(4, 5, 1),
  shape(5, 3, 2),
  shape(5, 4, 1),
  shape(5, 2, 3),
];

export const DEFAULT_FORMATION = FORMATIONS[0] as Formation;

export const findFormation = (id: string): Formation | null =>
  FORMATIONS.find((formation) => formation.id === id) ?? null;

/** How many of `position` this formation calls for. */
export const slotsFor = (formation: Formation, position: Position): number =>
  formation[position];

/**
 * The shape a picked eleven is already in.
 *
 * Used to label a team that came back from the API, which stores players but
 * not the formation they were picked in.
 */
export function describeShape(counts: Record<Position, number>): string {
  return `${counts.DEF}-${counts.MID}-${counts.FWD}`;
}
